import React, { useEffect, useRef, useState } from "react";

import ClozeSentence from "../ClozeSentence";
import Icon from "../Icon";
import EntryDropdown from "./EntryDropdown";
import EntryExplanation from "./EntryExplanation";
import useClozeSelection from "./useClozeSelection";

const TEXT_CLOZE_RE = /\{\{([^{}]*)\}\}/;

function plainTitle(text) {
  if(!text) return "";
  const m = text.match(TEXT_CLOZE_RE);
  return m ? m[1] : text;
}

export default function Entry(props) {
  const { entry, onDelete, onImportSingle, onToggleSelect, onUpdate, readOnly, selected, targetLanguageIso } = props;

  const [editing, setEditing] = useState(false);
  const [sentence, setSentence] = useState(entry.sentence || "");
  const [sentenceTranslation, setSentenceTranslation] = useState(entry.sentenceTranslation || "");
  const [saving, setSaving] = useState(false);
  const fetchExplanationRef = useRef(null);
  const { onSelect: onSentenceSelect, ref: sentenceRef } = useClozeSelection(setSentence);

  useEffect(() => {
    if(!editing) {
      setSentence(entry.sentence || "");
      setSentenceTranslation(entry.sentenceTranslation || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.sentence, entry.sentenceTranslation]);

  const isProcessing = entry.status === "pending" || entry.status === "queued" || entry.status === "working";
  const isProcessed = entry.status === "processed";
  const isFailed = entry.status === "failed";
  const word = plainTitle(entry.text);

  const startEdit = () => {
    setSentence(entry.sentence || "");
    setSentenceTranslation(entry.sentenceTranslation || "");
    setEditing(true);
  };

  const saveEdit = (e) => {
    e.preventDefault();
    setSaving(true);

    $.ajax({
      contentType: "application/json",
      data: JSON.stringify({
        quick_capture_entry: {
          sentence,
          sentence_translation: sentenceTranslation
        }
      }),
      dataType: "json",
      method: "patch",
      url: entry.url
    })
      .done((data) => {
        if(data.errors) {
          setSaving(false);
          alert(data.errors.join("\n"));
          return;
        }
        setSaving(false);
        setEditing(false);
        onUpdate && onUpdate(data.quickCaptureEntry);
      })
      .fail(() => {
        setSaving(false);
        alert("Failed to save. Please try again.");
      });
  };

  const renderBody = () => {
    if(isProcessing) {
      // A capture that already has a cloze is already the sentence - only the
      // translation is on its way - so show it rather than a placeholder that
      // leaves just the bold cloze word on screen.
      if(TEXT_CLOZE_RE.test(entry.text || "")) {
        return (
          <div>
            <div><ClozeSentence text={entry.text} /></div>
            <div className="text-muted small"><Icon name="hourglass" /> Translating...</div>
          </div>
        );
      }

      return (
        <div className="text-muted">
          <Icon name="hourglass" /> Generating sentence...
        </div>
      );
    }
    if(isFailed) {
      return <div className="text-danger small">Failed to generate sentence.</div>;
    }
    if(isProcessed) {
      return (
        <div>
          {entry.sentence && <div><ClozeSentence text={entry.sentence} /></div>}
          {entry.sentenceTranslation && <div className="text-muted small">{entry.sentenceTranslation}</div>}
        </div>
      );
    }
    return null;
  };

  const renderEditForm = () => (
    <form onSubmit={saveEdit}>
      <div className="form-group">
        <label>Sentence</label>
        <textarea
          aria-label="Sentence"
          className="form-control"
          disabled={saving}
          maxLength={300}
          onChange={(e) => setSentence(e.target.value)}
          onSelect={onSentenceSelect}
          ref={sentenceRef}
          rows={2}
          value={sentence}
        />
        <p className="help-block small">
          Highlight text - or double click a word - to make it the cloze. The cloze is wrapped in double braces, e.g. <code>{"This is an {{example}}."}</code>
        </p>
      </div>
      <div className="form-group">
        <label>Translation</label>
        <textarea
          className="form-control"
          disabled={saving}
          maxLength={300}
          onChange={(e) => setSentenceTranslation(e.target.value)}
          rows={2}
          value={sentenceTranslation}
        />
      </div>
      <div>
        <button className="btn btn-success" disabled={saving} type="submit">Save</button>
        {" "}
        <button className="btn btn-link" disabled={saving} onClick={() => setEditing(false)} type="button">Cancel</button>
      </div>
    </form>
  );

  return (
    <li className="list-group-item">
      <div className="row">
        <div className="col-xs-1">
          <input
            aria-label={`Select ${word}`}
            checked={!!selected}
            disabled={isFailed || readOnly}
            onChange={() => onToggleSelect(entry.id)}
            type="checkbox"
          />
        </div>
        <div className="col-xs-9">
          <div><strong>{word}</strong></div>
          {editing ? renderEditForm() : renderBody()}
          {entry.explanationUrl && (
            <EntryExplanation explanationUrl={entry.explanationUrl} onFetchRef={fetchExplanationRef} />
          )}
        </div>
        <div className="col-xs-2 text-right">
          {!editing && isProcessed && !readOnly && (
            <EntryDropdown
              entry={entry}
              onDelete={() => onDelete(entry.id)}
              onEdit={startEdit}
              onExplain={() => fetchExplanationRef.current && fetchExplanationRef.current()}
              onImport={() => onImportSingle(entry.id)}
              targetLanguageIso={targetLanguageIso}
              word={word}
            />
          )}
          {!editing && !isProcessed && !readOnly && (
            <button className="btn btn-default btn-xs" onClick={() => onDelete(entry.id)} title="Delete" type="button">
              <Icon name="trash" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
